import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { useEffectOnce } from 'react-use';
import { useSearchParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import styled from 'styled-components';
import _ from 'lodash';

import FieldMap from '../field-map/FieldMap';
import SidePanelBase, { PanelSection } from '../SidePanel';
import Logo from '../icons/Logo';
import { fromReadableUnitName } from '../../constants/units';
import { UNIT_OPTIONS, useUnitContext } from '../../contexts/UnitContext';
import { useFarmAddress, useFarmName } from '../../utils/hooks';
import {
  useDisableMapRotation,
  useMapCenter,
  useMapScale,
  useMapSectionLabels,
} from './utils/map';
import { useChunksByHeights } from './utils/content';
import {
  useLayerAndSectionStateFromSearchParams,
  useResetItemVisibility,
} from './utils/visibility';
import { captureEvent } from '../../utils/analytics';
import { StyledLatLng } from '../map/CursorLatLng';
import {
  BORDER_HEIGHT,
  INSTRUCTIONS_HEADER_HEIGHT,
  PAGE_HEIGHT_INCHES,
  PAGE_WIDTH_INCHES,
  PANEL_HEIGHT,
} from './constants';
import { useCropsInfo } from './useCropsInfo';
import { useLayersInfo } from './useLayersInfo';
import { useSectionsInfo } from './useSectionsInfo';
import { useNotesInfo } from './useNotesInfo';
import { useSoilLayerLegendInfo } from './useSoilLayerLegendInfo';
import {
  FieldsPreviewContext,
  FieldsPreviewContextProvider,
} from './components';
import { useSummaryInfo } from './useSummaryInfo';
import { MapContext } from '../../contexts/MapContext';
import useTitleInfo from './useTitleInfo';

const Container = styled.div`
  display: flex;
  flex-direction: column;

  position: relative;

  width: 100%;
  height: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;

const Content = styled.div`
  display: flex;

  width: 100%;
  height: 100%;
  overflow: hidden;

  /* required due to flexbox weirdness */
  min-height: 0;
`;

const SidePanel = styled(SidePanelBase)`
  position: relative;
`;

const InstructionsHeader = styled.div`
  display: flex;
  align-items: center;

  padding: 0 var(--content-x-padding);
  height: ${INSTRUCTIONS_HEADER_HEIGHT}px;
  flex-shrink: 0;

  background: white;
  border-bottom: 0.5px solid var(--border-color);
`;

const Border = styled.div`
  background: var(--side-panel-color);
  flex-shrink: 0;
  height: ${BORDER_HEIGHT}px;
`;

const PageNumberFooter = styled.div`
  padding: 0 var(--side-panel-padding);
  text-align: right;
  width: 100%;
`;

// By keeping the sides of the header equal width, the title will be centered
const HeaderSideContent = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  width: 245px;

  ${({ left }) => left && 'justify-content: flex-start;'}
  ${({ right }) => right && 'justify-content: flex-end;'}
`;
const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  /* Hide some map controls */
  .mapboxgl-ctrl-bottom-left,
  .mapboxgl-ctrl-bottom-right,
  .mapboxgl-ctrl-compass,
  ${StyledLatLng} {
    display: none;
  }

  /* Hide all map controls on print */
  .mapboxgl-control-container {
    ${({ isPrintMode }) =>
      isPrintMode ? `display: none` : `@media print { display: none; }`}
  }

  /* make map non-interactive in print mode */
  ${({ isPrintMode }) => (isPrintMode ? 'pointer-events: none;' : '')}

  /* add compass */
  .mapboxgl-map::after {
    content: '';

    background-image: url('/compass.png');
    background-size: contain;

    position: absolute;
    bottom: 32px;
    right: 8px;
    width: 40px;
    height: 40px;
  }

  /* Give the canvas responsive dimensions -- this fixes an issue where the dimensions of the page change slightly in print mode and the canvas does not update */
  canvas {
    width: 100% !important;
    height: 100% !important;
  }
`;

const MapInfoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  padding: 2px 5px;
  background-color: hsla(0, 0%, 100%, 0.8);
  font-size: 12px;

  display: flex;
  align-items: center;

  ${({ left }) =>
    left &&
    `
    left: 0;
    right: auto;
    max-width: 150px;
    `}
  ${({ right }) =>
    right &&
    `
    right: 0;
    left: auto;
    max-width: calc(100% - 150px); /* ensure that this doesn't overlap w/ the left-side MapInfoOverlay */
  `}
`;

/**
 * Sets up page & print styles to ensure the page has the dimensions of a landscape letter page.
 */
const printStyles = `
    /* Set the page dimensions for print to landscape */
    @page {
      size: 11in 8.5in;
      margin: 0;
    }

    .root {
      /* Prevent browser from removing background colors from print */
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;

      width: ${PAGE_WIDTH_INCHES}in;
      height: ${PAGE_HEIGHT_INCHES}in;

      overflow: visible;
      position: relative;
    }

    /* Set the body dimensions to match .root */
    body {
      width: ${PAGE_WIDTH_INCHES}in;
      height: ${PAGE_HEIGHT_INCHES}in;

      /* overrule a print-only min-width that's set in the global styles */
      min-width: unset !important;

      /**
       * Avoid the weirdness that can happen due to browser print dialog margin settings.
       * 13px divided by 96px/in = 0.1354167in
       */
      font-size: 0.1354167in;
    }

    /* Indicate the part of the viewport that is not printed */
    html {
      background-color: lightgray;
    }
`;

const Key = styled.kbd`
  padding: 0.1rem 0.3rem;
`;

const ToggleShortcutMessage = styled.div`
  line-height: 17px;
  width: 130px;
`;

const InlineTextButton = styled(Button)`
  padding: 0;
  border: none;
  vertical-align: baseline;
`;

const unitTypeIsValid = (unitType) =>
  unitType && UNIT_OPTIONS.map((o) => o.value).includes(unitType);

const getElements = () => [
  ...document.querySelectorAll(PanelSection.toString()),
];
const getElementHeights = () =>
  getElements().map((elem) => elem?.clientHeight ?? 0);

/**
 * Element that copies the map over to another canvas.
 */
const MapCopy = () => {
  const ref = useRef();

  const {
    mapState: { mapRef, mapLoaded },
  } = useContext(MapContext);

  useEffect(() => {
    if (!mapLoaded) return null;

    const map = mapRef.current;
    const container = map.getContainer();
    const sourceCanvas = container.querySelector('canvas');

    const destinationCanvas = ref.current;

    if (!sourceCanvas || !destinationCanvas) return null;

    const copy = () => {
      destinationCanvas.height = sourceCanvas.height;
      destinationCanvas.width = sourceCanvas.width;

      destinationCanvas.getContext('2d').drawImage(sourceCanvas, 0, 0);
    };
    copy();

    map.on('render', copy);
    return () => {
      map.off('render', copy);
    };
  }, [mapLoaded, mapRef]);

  return (
    <MapContainer>
      <div className="w-100 h-100 mapboxgl-map">
        <canvas ref={ref} className="d-block" />
      </div>
    </MapContainer>
  );
};

const DoublePage = ({
  isFirstPage,
  farmAddress,
  mapCenter,
  pageIndex,
  pageTotal,
  leftPanelContent,
  rightPanelContent,
}) => {
  const {
    isPrintMode,
    setIsPrintMode,
    resetPanelVisibility,
    panelVisibility,
  } = useContext(FieldsPreviewContext);

  const {
    layers: { layers },
    fields: { fieldSections },
  } = useContext(MapContext);

  const handlePrint = async () => {
    captureEvent('preview-page-print-pdf-click');

    const initialPrintMode = isPrintMode;

    // If needed, enter print mode:
    if (!initialPrintMode) {
      setIsPrintMode(true);
      await new Promise((resolve) => setTimeout(resolve, 250));
    }

    window.print();

    // If needed, restore print mode state:
    if (!initialPrintMode) {
      setIsPrintMode(false);
    }
  };

  const hasVisibilityChanges = useMemo(
    () =>
      Object.values(panelVisibility).some((shown) => !shown) ||
      Object.values(layers).some((l) => !l.shown) ||
      fieldSections.some((s) => !s.shown),

    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is okay since panelVisibility has a constant number of values
    [...Object.values(panelVisibility), layers, fieldSections],
  );

  const resetItemVisibility = useResetItemVisibility();

  const resetVisibility = () => {
    resetPanelVisibility();
    resetItemVisibility();
  };

  return (
    <>
      {!isPrintMode && isFirstPage && (
        <InstructionsHeader isPrintMode={isPrintMode}>
          <HeaderSideContent left>
            <div className="text-muted pl-1">
              Double click project name to edit title.
            </div>
          </HeaderSideContent>

          <div className="flex-grow-1 text-center px-2">
            <div className="text-muted">
              Click headers (i.e. &quot;Crops&quot;) or individual
              sections/layers to toggle visibility.{' '}
              {hasVisibilityChanges && (
                <InlineTextButton
                  color="link"
                  size="sm"
                  onClick={resetVisibility}
                >
                  Reset.
                </InlineTextButton>
              )}
            </div>
          </div>

          <HeaderSideContent right>
            <Button onClick={handlePrint} className="flex-shrink-0">
              Print as PDF
            </Button>
            <ToggleShortcutMessage className="p-2 ml-2 text-muted">
              or press <Key>H</Key> to toggle print mode.
            </ToggleShortcutMessage>
          </HeaderSideContent>
        </InstructionsHeader>
      )}
      <Container>
        <Border />

        <Content>
          <SidePanel left>{...leftPanelContent}</SidePanel>

          <MapContainer isPrintMode={isPrintMode}>
            {isFirstPage ? <FieldMap readonly /> : <MapCopy />}

            <MapInfoOverlay left>
              <div className="mr-2">
                <Logo width={12} height={12} />
              </div>
              <div>Made with Overyield</div>
            </MapInfoOverlay>

            <MapInfoOverlay right className="text-truncate d-block">
              {farmAddress}
              &nbsp;&nbsp;
              {mapCenter.map((n) => n.toFixed(7)).join(', ')}
            </MapInfoOverlay>
          </MapContainer>

          <SidePanel className="d-flex justify-content-between" right>
            <div>{...rightPanelContent}</div>
            <PageNumberFooter>
              Page {pageIndex + 1} of {pageTotal}
            </PageNumberFooter>
          </SidePanel>
        </Content>

        <Border />
      </Container>
    </>
  );
};

const FieldsPreview = () => {
  // On pageload, capture event & load units from URL query params
  useEffectOnce(() => {
    captureEvent('preview-page-view');
  });

  const [searchParams] = useSearchParams();

  // Get the units from the URL query params
  const { setUnits, units } = useUnitContext();
  const unitType = fromReadableUnitName(searchParams.get('units'));
  useEffect(() => {
    if (unitTypeIsValid(unitType) && unitType !== units) {
      setUnits(unitType);
    }
  }, [unitType, units, setUnits]);

  const { togglePrintMode, panelVisibility } = useContext(FieldsPreviewContext);
  const {
    mapState: { mapLoaded, mapRef },
  } = useContext(MapContext);

  useHotkeys('h', togglePrintMode, [togglePrintMode]);

  const farmName = useFarmName();
  const farmAddress = useFarmAddress();

  // Set title to farm name so that PDF has that filename on print
  useEffect(() => {
    document.title = farmName;
  }, [farmName]);

  // Add a scale to the map
  useMapScale();

  // Show section labels on map
  useMapSectionLabels();

  // Set layer visibility, section visibility, and base map from the URL search params.
  useLayerAndSectionStateFromSearchParams();

  // Disable map rotation via control-click
  useDisableMapRotation();

  const mapCenter = useMapCenter(mapLoaded, mapRef);

  // We split the side panel content dynamically into columns based on the height of the content.
  const content = [
    // These hooks each return arrays of JSX elements.
    ...useTitleInfo(),
    ...useSummaryInfo(),
    ...useCropsInfo(),
    ...useLayersInfo(),
    ...useSectionsInfo(),
    ...useSoilLayerLegendInfo(),
    ...useNotesInfo(),
  ];
  const [contentHeights, setContentHeights] = useState([]);
  const chunkedContent = useChunksByHeights(
    content,
    contentHeights,
    PANEL_HEIGHT,
  );

  useEffect(() => {
    // Set up ResizeObserver to watch the panel elements for changes in height, and call updateContentHeights when they do:
    const observer = new ResizeObserver(() => {
      setContentHeights(getElementHeights());
    });

    // Observe the elements
    getElements().forEach((elem) => {
      observer.observe(elem);
    });

    // cleanup
    return () => {
      observer.disconnect();
    };
  }, [content.length]);

  // Update content heights when panel visibility changes
  useEffect(() => {
    setContentHeights(getElementHeights());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- this is okay since panelVisibility has a constant number of values
  }, Object.values(panelVisibility));

  const doublePages = Math.max(1, Math.ceil(chunkedContent.length / 2));

  return (
    <>
      <style>{printStyles}</style>

      {_.range(doublePages).map((i) => (
        <DoublePage
          key={i}
          isFirstPage={i === 0}
          farmAddress={farmAddress}
          mapCenter={mapCenter}
          pageIndex={i}
          pageTotal={doublePages}
          leftPanelContent={chunkedContent[i * 2] ?? []}
          rightPanelContent={chunkedContent[i * 2 + 1] ?? []}
        />
      ))}
    </>
  );
};

const FieldsPreviewWrapped = () => (
  <FieldsPreviewContextProvider>
    <FieldsPreview />
  </FieldsPreviewContextProvider>
);

export default FieldsPreviewWrapped;
